import { t } from "@/controllerLK/AvalLocalization";

const TIME_UNITS = {
    SECOND: 1,
    MINUTE: 60,
    HOUR: 3600,
    DAY: 86400,
    WEEK: 604800,
    MONTH: 2592000,
    YEAR: 31536000,
};

function getPluralForm(number, one, few, many) {
    const absNumber = Math.abs(number);
    const lastDigit = absNumber % 10;
    const lastTwoDigits = absNumber % 100;

    if (lastDigit === 1 && lastTwoDigits !== 11) {
        return one;
    }

    if (lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)) {
        return few;
    }

    return many;
}

function formatTimeInterval(diffSeconds) {
    const intervals = [
        {
            threshold: TIME_UNITS.MINUTE,
            unit: TIME_UNITS.MINUTE,
            keys: ["minutes.one", "minutes.few", "minutes.many"],
            returnEarly: () => t("date_time_tools.get_time_ago.lesser_minute"),
        },
        {
            threshold: TIME_UNITS.HOUR,
            unit: TIME_UNITS.MINUTE,
            keys: ["minutes.one", "minutes.few", "minutes.many"],
        },
        {
            threshold: TIME_UNITS.DAY,
            unit: TIME_UNITS.HOUR,
            keys: ["hour.one", "hour.few", "hour.many"],
        },
        {
            threshold: TIME_UNITS.WEEK,
            unit: TIME_UNITS.DAY,
            keys: ["day.one", "day.few", "day.many"],
        },
        {
            threshold: TIME_UNITS.MONTH,
            unit: TIME_UNITS.WEEK,
            keys: ["week.one", "week.few", "week.many"],
        },
        {
            threshold: TIME_UNITS.YEAR,
            unit: TIME_UNITS.MONTH,
            keys: ["month.one", "month.few", "month.many"],
        },
        {
            threshold: Infinity,
            unit: TIME_UNITS.YEAR,
            keys: ["year.one", "year.few", "year.many"],
        },
    ];

    for (const interval of intervals) {
        if (diffSeconds < interval.threshold) {
            if (interval.returnEarly) {
                return interval.returnEarly();
            }

            const value = Math.floor(diffSeconds / interval.unit);
            const form = getPluralForm(
                value,
                t(`date_time_tools.get_time_ago.${interval.keys[0]}`),
                t(`date_time_tools.get_time_ago.${interval.keys[1]}`),
                t(`date_time_tools.get_time_ago.${interval.keys[2]}`)
            );

            return `${value} ${form} ${t("date_time_tools.get_time_ago.ago_title")}`;
        }
    }
}

export function getTimeAgo(dateTimeString) {
    let diffSeconds;

    if (typeof dateTimeString === "number") {
        // Unix timestamp в секундах
        const nowSeconds = Math.floor(Date.now() / 1000);
        diffSeconds = nowSeconds - dateTimeString;
    } else {
        // ISO строка даты
        const now = new Date();
        const pastDate = new Date(dateTimeString);
        diffSeconds = Math.floor((now - pastDate) / 1000);
    }

    return formatTimeInterval(diffSeconds);
}

export function getTime(date) {
    let dateObj;

    if (typeof date === "number") {
        // Unix timestamp в секундах
        dateObj = new Date(date * 1000);
    } else if (date instanceof Date) {
        dateObj = date;
    } else {
        // Строка даты
        dateObj = new Date(date);
    }

    const hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");

    return `${hours}:${minutes}`;
}

export function toTimestamp(date) {
    if (!(date instanceof Date)) {
        throw new TypeError("Invalid date object");
    }
    return Math.floor(date.getTime() / 1000);
}

/**
 * Возвращает unix-timestamp (секунды) для 00:00:00 UTC+3 того дня,
 * в который попадает переданный timestamp.
 */
export function startOfDayUTC3(timestamp) {
    const UTC3_OFFSET = 3 * 3600;
    const localTime = timestamp + UTC3_OFFSET;
    return Math.floor(localTime / 86400) * 86400 - UTC3_OFFSET;
}

/**
 * Возвращает unix-timestamp (секунды) для 23:59:00 UTC+3 того дня,
 * в который попадает переданный timestamp.
 */
export function endOfDayUTC3(timestamp) {
    return startOfDayUTC3(timestamp) + 23 * 3600 + 59 * 60;
}

export function convertTimestampToData(unixTimestamp) {
    const date = new Date(unixTimestamp * 1000);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");

    return `${day}.${month}.${year} ${hours}:${minutes}`;
}